'use client';

import { ArrowLeft, X } from 'lucide-react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { useCallback, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { pwaRadius } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

const SWIPE_CLOSE_THRESHOLD_PX = 72;

export type PwaBottomSheetProps = {
  open: boolean;
  onClose: () => void;
  /** Для aria-labelledby; при headerSlot можно задать краткий заголовок для скринридеров */
  title: string;
  subtitle?: string | null;
  children: ReactNode;
  footer?: ReactNode;
  /** Полностью заменяет стандартную строку заголовка (крестик/название). */
  headerSlot?: ReactNode;
  /**
   * back — стрелка слева; close — крестик справа.
   * По умолчанию undefined: закрытие свайпом вниз, по overlay и системной «назад».
   */
  leading?: 'close' | 'back';
  /** Для leading=back: по стрелке вызывается onBack, иначе onClose */
  onBack?: () => void;
  /** Полоска-свайп сверху панели (как в нативных sheet) */
  showDragHandle?: boolean;
  /** Доп. классы на прокручиваемую область */
  contentClassName?: string;
  /** Класс на обёртку footer (например без border, стекло) */
  footerClassName?: string;
  /** Класс на корневой overlay (z-index и т.д.) */
  overlayClassName?: string;
  className?: string;
  /**
   * Один скролл: ручка, headerSlot и children в одной прокручиваемой колонке
   * (шапка уезжает вверх вместе с контентом).
   */
  unifiedScroll?: boolean;
};

/**
 * Нижняя панель: затемнение, скругление сверху ~28px, заголовок.
 */
export function PwaBottomSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  headerSlot,
  leading,
  onBack,
  showDragHandle = true,
  contentClassName,
  footerClassName,
  overlayClassName,
  className,
  unifiedScroll = false,
}: PwaBottomSheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartYRef = useRef<number | null>(null);
  const dragActiveRef = useRef(false);
  const [dragOffsetY, setDragOffsetY] = useState(0);

  const resetDrag = useCallback(() => {
    dragStartYRef.current = null;
    dragActiveRef.current = false;
    setDragOffsetY(0);
  }, []);

  const handleDragHandlePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!showDragHandle) return;
    dragStartYRef.current = e.clientY;
    dragActiveRef.current = true;
    setDragOffsetY(0);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [showDragHandle]);

  const handleDragHandlePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragActiveRef.current || dragStartYRef.current == null) return;
    const delta = Math.max(0, e.clientY - dragStartYRef.current);
    setDragOffsetY(delta);
  }, []);

  const handleDragHandlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragActiveRef.current) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      const shouldClose = dragOffsetY >= SWIPE_CLOSE_THRESHOLD_PX;
      resetDrag();
      if (shouldClose) onClose();
    },
    [dragOffsetY, onClose, resetDrag]
  );

  const handleDragHandlePointerCancel = useCallback(() => {
    resetDrag();
  }, [resetDrag]);

  if (!open) return null;

  const dragHandle = showDragHandle ? (
    <div
      className="flex shrink-0 cursor-grab touch-none justify-center pt-2.5 pb-1 active:cursor-grabbing"
      aria-hidden
      onPointerDown={handleDragHandlePointerDown}
      onPointerMove={handleDragHandlePointerMove}
      onPointerUp={handleDragHandlePointerUp}
      onPointerCancel={handleDragHandlePointerCancel}
    >
      <span className="h-1 w-11 rounded-full bg-[var(--border-strong)] opacity-80" />
    </div>
  ) : null;

  /** Портал в body: иначе z-index оказывается внутри stacking context страницы (overflow) и sheet уходит под нижнюю навигацию (z-40). */
  const overlay = (
    <div
      className={cn(
        'fixed inset-0 z-[100] flex items-end justify-center bg-black/50 pt-[max(0.75rem,env(safe-area-inset-top,0px))]',
        overlayClassName
      )}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={cn(
          'flex max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-1.25rem))] w-full max-w-lg flex-col overflow-hidden border-t border-[var(--border)] bg-[var(--surface)] shadow-[0_-8px_40px_rgba(15,23,42,0.12)] dark:shadow-[0_-8px_40px_rgba(0,0,0,0.45)]',
          pwaRadius.sheet,
          className
        )}
        style={
          dragOffsetY > 0
            ? { transform: `translateY(${dragOffsetY}px)`, transition: dragActiveRef.current ? 'none' : undefined }
            : undefined
        }
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {unifiedScroll && headerSlot ? (
          <div
            className={cn(
              'min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain',
              contentClassName
            )}
          >
            {dragHandle}
            <h2 id={titleId} className="sr-only">
              {title}
            </h2>
            {headerSlot}
            <div className="px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">{children}</div>
          </div>
        ) : (
          <>
            {dragHandle}
            {headerSlot ? (
              <div className="shrink-0 border-b border-[var(--border)]">
                <h2 id={titleId} className="sr-only">
                  {title}
                </h2>
                {headerSlot}
              </div>
            ) : (
              <div
                className={cn(
                  'flex shrink-0 items-start justify-between gap-3 border-b border-[var(--border)] px-4 pb-3',
                  showDragHandle ? 'pt-1' : 'pt-3'
                )}
              >
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  {leading === 'back' ? (
                    <button
                      type="button"
                      onClick={() => (onBack ? onBack() : onClose())}
                      className="mt-0.5 shrink-0 rounded-xl p-1 text-[var(--muted-foreground)] transition hover:bg-[var(--surface-strong)]"
                      aria-label="Назад"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  ) : null}
                  <div className="min-w-0">
                    <h3 id={titleId} className="text-base font-bold text-[var(--foreground)]">
                      {title}
                    </h3>
                    {subtitle ? <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">{subtitle}</p> : null}
                  </div>
                </div>
                {leading === 'close' ? (
                  <button
                    type="button"
                    onClick={onClose}
                    className="shrink-0 rounded-xl p-1 text-[var(--muted-foreground)] transition hover:bg-[var(--surface-strong)]"
                    aria-label="Закрыть"
                  >
                    <X className="h-5 w-5" />
                  </button>
                ) : null}
              </div>
            )}

            <div
              className={cn(
                'min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3',
                contentClassName
              )}
            >
              {children}
            </div>
          </>
        )}

        {footer ? (
          <div
            className={cn(
              'shrink-0 border-t border-[var(--border)] bg-[var(--surface)] px-4 pt-3.5 pb-[max(1.125rem,env(safe-area-inset-bottom))] shadow-[0_-1px_0_rgba(15,23,42,0.04)] dark:shadow-[0_-1px_0_rgba(255,255,255,0.06)]',
              footerClassName
            )}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(overlay, document.body);
}
